import type {
  IBalanceDetails,
  ICardList,
  IChargingSession,
  IEditCardData,
  IEnrollment,
  IGetAPIKey,
  IGetCardList,
  IPayNow,
  IPaymentDetail,
  IPaymentInvoice,
  IPaymentInvoiceInput,
  IRefundRequestInput,
  IRefundRequestList,
  IRejectedPromise,
  ISavePaymentType,
} from '../../models/payment';
import type { IGetConfigurationData } from '../../models/profile';
import type { IRefundRequestForm, IWithdrawAmountForm } from '../../types/stateTypes';
import BaseService from '../base-service';
import { StandardResponse, createStandardResponse } from './../../models/standard-response';

export const getPaymentDetail = async (): Promise<StandardResponse<IPaymentDetail>> => {
  try {
    const response = await BaseService({
      url: `/driver/payment/details`,
      method: 'GET',
    });
    return createStandardResponse<IPaymentDetail>(response.status, response.data.data, response.data.message, null);
  } catch (error) {
    return Promise.reject(error.response.data);
  }
};

export const savePaymentType = async (data: ISavePaymentType): Promise<StandardResponse<number>> => {
  try {
    const res = await BaseService({
      url: `/driver/payment-type`,
      method: 'put',
      data,
    });
    return createStandardResponse<number>(res.status, res.data.data, res.data.message, null);
  } catch (e: unknown) {
    return Promise.reject((e as IRejectedPromise).response.data);
  }
};

export const payNow = async (data: IPayNow): Promise<StandardResponse<number>> => {
  try {
    const res = await BaseService({
      url: `/driver/payment/charge-amount`,
      method: 'post',
      data,
    });
    return createStandardResponse<number>(res.status, res.data.data, res.data.message, null);
  } catch (e) {
    return Promise.reject(e);
  }
};

export const EditCardData = async (data: IEditCardData): Promise<StandardResponse<Array<number>>> => {
  try {
    const res = await BaseService({
      url: '/driver/update-card',
      method: 'put',
      data,
    });
    return createStandardResponse<Array<number>>(res.status, res.data.data, res.data.message, null);
  } catch (e: unknown) {
    return Promise.reject((e as IRejectedPromise).response.data);
  }
};

export const getPublicConfig = async (data: IGetAPIKey): Promise<IGetConfigurationData> => {
  try {
    const res = await BaseService({
      url: '/driver/public/configuration/search',
      method: 'POST',
      data: { ...data, byPassAuth: true },
    });
    return Promise.resolve(res.data);
  } catch (e) {
    return Promise.reject(e);
  }
};
export const getCardList = async (data: IGetCardList): Promise<StandardResponse<ICardList>> => {
  try {
    const res = await BaseService({
      url: `/driver/refund/cards`,
      method: 'POST',
      data,
    });
    return createStandardResponse<ICardList>(res.status, res.data.data, res.data.message, null);
  } catch (e) {
    return Promise.reject(e);
  }
};

export const withdrawMoney = async (data): Promise<StandardResponse<IEnrollment>> => {
  try {
    const res = await BaseService({
      url: '/driver/payment/withdraw-money',
      method: 'POST',
      data,
    });
    return createStandardResponse<IEnrollment>(res.status, res.data.data, res.data.message, null);
  } catch (e) {
    return Promise.reject(e);
  }
};

export const reFundRequest = async (
  data: IRefundRequestForm | IWithdrawAmountForm,
): Promise<StandardResponse<number>> => {
  try {
    const res = await BaseService({
      url: '/driver/refund-requests',
      method: 'POST',
      data,
    });
    return createStandardResponse<number>(res.status, res.data.data, res.data.message, null);
  } catch (e) {
    return Promise.reject(e);
  }
};
export const refundRequestList = async (data: IRefundRequestInput): Promise<StandardResponse<IRefundRequestList>> => {
  try {
    const res = await BaseService({
      url: '/driver/refund-requests/search',
      method: 'POST',
      data,
    });
    return createStandardResponse<IRefundRequestList>(res.status, res.data.data, res.data.message, null);
  } catch (e) {
    return Promise.reject(e);
  }
};
export const cancelRequest = async (id: number): Promise<StandardResponse<number>> => {
  try {
    const res = await BaseService({
      url: `/driver/refund-requests/${id}`,
      method: 'PUT',
    });
    return createStandardResponse<number>(res.status, res.data.data, res.data.message, null);
  } catch (e) {
    return Promise.reject(e);
  }
};

export const createPaymentInvoice = async (data: IPaymentInvoiceInput): Promise<StandardResponse<IPaymentInvoice>> => {
  try {
    const res = await BaseService({
      url: '/driver/payment/create/invoice',
      method: 'POST',
      data,
    });
    return createStandardResponse<IPaymentInvoice>(res.status, res.data.data, res.data.message, null);
  } catch (e) {
    return Promise.reject(e);
  }
};

export const getTransactionDetails = async (invoiceID): Promise<StandardResponse<IBalanceDetails>> => {
  try {
    const res = await BaseService({
      url: `/driver/payment/invoice/${invoiceID}`,
      method: 'GET',
    });
    return createStandardResponse<IBalanceDetails>(res.status, res.data.data, res.data.message, null);
  } catch (e) {
    return Promise.reject(e);
  }
};

export const getDatatransTransactionDetails = async (invoiceID): Promise<StandardResponse<IBalanceDetails>> => {
  try {
    const res = await BaseService({
      url: `/driver/payment-datatrans/invoice/${invoiceID}`,
      method: 'GET',
    });
    return createStandardResponse<IBalanceDetails>(res.status, res.data.data, res.data.message, null);
  } catch (e) {
    return Promise.reject(e);
  }
};

export const createDatatransInvoice = async (
  data: IPaymentInvoiceInput,
): Promise<StandardResponse<IPaymentInvoice>> => {
  try {
    const res = await BaseService({
      url: '/driver/payment-datatrans/create/invoice',
      method: 'POST',
      data,
    });
    return createStandardResponse<IPaymentInvoice>(res.status, res.data.data, res.data.message, null);
  } catch (e) {
    return Promise.reject(e);
  }
};

export const getChargingSession = async (): Promise<StandardResponse<IChargingSession>> => {
  try {
    const res = await BaseService({
      url: `/driver/current-session`,
      method: 'GET',
    });
    return createStandardResponse<IChargingSession>(res.status, res.data.data, res.data.message, null);
  } catch (e) {
    return Promise.reject(e);
  }
};
